import { Box, CloseButton, Dialog, Portal, Text } from '@chakra-ui/react';
import { ChevronLeftIcon } from '@/assets/icons';
import type {
	CustomDialogBackdropProps,
	CustomDialogCloseTriggerProps,
	CustomDialogContentProps,
	CustomDialogDescriptionProps,
	CustomDialogPositionerProps,
	CustomDialogTitleProps,
	DialogOpenChangeDetails,
	FaceliftDialogProps,
} from '@/components/Atoms/FaceliftDialog/FaceliftDialog.types';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show';
import { colors } from '@/components/ui/theme';

const CustomDialog = {
	Backdrop: (props: CustomDialogBackdropProps) => (
		<Dialog.Backdrop {...props} />
	),
	Positioner: (props: CustomDialogPositionerProps) => (
		<Dialog.Positioner {...props} />
	),
	Content: (props: CustomDialogContentProps) => <Dialog.Content {...props} />,
	Title: (props: CustomDialogTitleProps) => <Dialog.Title {...props} />,
	Description: (props: CustomDialogDescriptionProps) => (
		<Dialog.Description {...props} />
	),
	CloseTrigger: (props: CustomDialogCloseTriggerProps) => (
		<Dialog.CloseTrigger {...props} />
	),
};

const FaceliftDialog = ({
	open,
	onOpenChange,
	title,
	subtitle,
	children,
	footer,
	showCloseBtn = false,
	showBackBtn = false,
	bg,
	color,
	sx,
	childrenSx,
	contentProps,
	...rest
}: FaceliftDialogProps) => {
	return (
		<Dialog.Root
			open={open}
			onOpenChange={(e: DialogOpenChangeDetails) => onOpenChange(e.open)}
			placement="center"
			{...rest}
		>
			<Portal>
				<CustomDialog.Backdrop style={{ background: 'rgba(0,0,0,0.1)' }} />

				<CustomDialog.Positioner> 
					<CustomDialog.Content
						boxShadow="0px 4px 4px 0px #00000040"
						bg={bg}
						color={color}
						style={{ position: 'relative', ...sx }}
						{...contentProps}
					>
						<Show when={!!title}>
							<Dialog.Header display="flex" flexDirection="column" _dark={{ bg: colors.formBgDark }}>
								<CustomDialog.Title>
									<Box display="flex" alignItems="center" gap={4}>
										<Show when={!!showBackBtn}>
											<Box
												onClick={() => onOpenChange(false)}
												bg="linear-gradient(90deg, {colors.brand.solid} 0%, {colors.brand.solidSecondary} 100%)"
												color="white"
												padding={2}
												borderRadius="full"
												cursor="pointer"
											>
												<ChevronLeftIcon width={10} height={10} />
											</Box>
										</Show>
										<FaceliftText fontWeight={600} fontSize="x-large">
											{title}
										</FaceliftText>
									</Box>
								</CustomDialog.Title>
								<Show when={!!subtitle}>
									<CustomDialog.Description>
										<Text fontSize="sm" color="gray.500">
											{subtitle}
										</Text>
									</CustomDialog.Description>
								</Show>
							</Dialog.Header>
						</Show>

						<Show when={!!children}>
							<Dialog.Body _dark={{ bg: colors.formBgDark }} {...(childrenSx && { style: { ...childrenSx } })}>{children}</Dialog.Body>
						</Show>

						<Show when={!!footer}>
							<Dialog.Footer _dark={{ bg: colors.formBgDark }}>{footer}</Dialog.Footer>
						</Show>

						<Show when={!!showCloseBtn}>
							<CustomDialog.CloseTrigger>
								<CloseButton
									position="absolute"
									top={4}
									right={4}
									size="sm"
									bg="linear-gradient(90deg, {colors.brand.solid} 0%, {colors.brand.solidSecondary} 100%)"
									color="white"
									borderRadius="full"
									_hover={{
										bg: 'linear-gradient(90deg, {colors.brand.solid} 0%, {colors.brand.solidSecondary} 100%)',
										opacity: 0.9,
									}}
									_active={{
										transform: 'scale(0.98)',
									}}
									aria-label="Close dialog"
								/>
							</CustomDialog.CloseTrigger>
						</Show>
					</CustomDialog.Content>
				</CustomDialog.Positioner>
			</Portal>
		</Dialog.Root>
	);
};

export default FaceliftDialog;
